import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Users, MessageSquare, FileText, Calendar, Activity } from 'lucide-react';
import { GroupDiscussionBoard } from '@/components/groups/GroupDiscussionBoard';
import { GroupResourceLibrary } from '@/components/groups/GroupResourceLibrary';
import { GroupActivityFeed } from '@/components/groups/GroupActivityFeed';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

export default function GroupDetail() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [group, setGroup] = useState<any>(null);
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (groupId) {
      loadGroup();
      loadMembers();
    }
  }, [groupId]);

  const loadGroup = async () => {
    if (!groupId) return;
    try {
      const { data, error } = await supabase
        .from('user_groups')
        .select('*')
        .eq('id', groupId)
        .single();

      if (error) throw error;
      setGroup(data);
    } catch (error) {
      console.error('Error loading group:', error);
      toast.error('Failed to load group');
    } finally {
      setLoading(false);
    }
  };

  const loadMembers = async () => {
    if (!groupId) return;
    try {
      const { data, error } = await supabase
        .from('user_group_members')
        .select('*, profiles(first_name, last_name, avatar_url)')
        .eq('group_id', groupId);

      if (error) throw error;
      setMembers(data || []);
    } catch (error) {
      console.error('Error loading members:', error);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (!group) return <div>Group not found</div>;

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <Button variant="ghost" size="sm" onClick={() => navigate('/my-groups')} className="mb-4">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Groups
      </Button>

      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">{group.name}</h1>
        <p className="text-muted-foreground">{group.description}</p>
        <div className="flex items-center gap-4 mt-3">
          <Badge variant="outline">
            <Users className="h-3 w-3 mr-1" />
            {members.length} members
          </Badge>
          <Badge variant="outline">{group.group_type}</Badge>
        </div>
      </div>

      <Tabs defaultValue="discussions" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="discussions">
            <MessageSquare className="h-4 w-4 mr-2" />
            Discussions
          </TabsTrigger>
          <TabsTrigger value="resources">
            <FileText className="h-4 w-4 mr-2" />
            Resources
          </TabsTrigger>
          <TabsTrigger value="activity">
            <Activity className="h-4 w-4 mr-2" />
            Activity
          </TabsTrigger>
          <TabsTrigger value="members">
            <Users className="h-4 w-4 mr-2" />
            Members
          </TabsTrigger>
        </TabsList>

        <TabsContent value="discussions" className="mt-4">
          <GroupDiscussionBoard groupId={groupId!} />
        </TabsContent>

        <TabsContent value="resources" className="mt-4">
          <GroupResourceLibrary groupId={groupId!} />
        </TabsContent>

        <TabsContent value="activity" className="mt-4">
          <GroupActivityFeed groupId={groupId!} />
        </TabsContent>

        <TabsContent value="members" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Group Members</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3">
                {members.map((member) => (
                  <div key={member.id} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-sm font-medium">
                        {member.profiles?.first_name?.[0]}{member.profiles?.last_name?.[0]}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm">
                        {member.profiles?.first_name} {member.profiles?.last_name}
                      </div>
                      <div className="text-xs text-muted-foreground capitalize">{member.role}</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}